import React, { useEffect, useRef, useState } from "react";
import { DataGrid } from "@mui/x-data-grid";
import { Alert, Menu, MenuItem, Snackbar, Tooltip } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEllipsisVertical,
  faMagnifyingGlass,
} from "@fortawesome/free-solid-svg-icons";
import { ChevronDown, ChevronLeft, ChevronRight, Filter, List } from "lucide-react";
import { Modal } from "react-bootstrap";
import moment from "moment";
import axios from "axios";
import { useContextStateManagement } from "../../context/ContextApi";
import {
  getInboundCallSummaries,
  getInboundCallSummariesPaginated,
  markSummaryRead as markSummaryReadApi,
} from "../../apiFunctions/summaryApi";
import { fetchRecordingPlaybackUrl } from "../../apiFunctions/recordingMediaApi";
import {
  DEFAULT_PRACTICE_TIMEZONE,
  formatInPracticeTimezone,
  formatUsDateInPracticeTimezone,
} from "../../practiceTimezone";
import { maskPhone } from "../../inboundCallHelpers";
import DateRangePicker from "../../components/Home/DateRangePicker";
import { PageActionMenu } from "../../components/PageActionMenu/PageActionMenu";
import CallFilterDialog from "../CallDialogs/CallFilterDialog";
import ClientAlertConfigModal from "../CallDialogs/ClientAlertConfigModal";
import "./InboundCallSummary.css";

// Release traceability badge (CONTRACT.md section 10) - same rendering logic as
// VoiceOpsPage.jsx's shortDigest/formatConfigSourceLabel/releaseBadgeTone/renderReleaseBadge
// (kept local rather than imported so this page doesn't pull in VoiceOpsPage.css as a side
// effect of importing from VoiceOpsPage.jsx). See styles under ".summary-release-pill" in
// InboundCallSummary.css.
function shortDigest(digest) {
  const value = String(digest || "").trim();
  if (!value) return "";
  const withoutPrefix = value.startsWith("sha256:") ? value.slice(7) : value;
  return withoutPrefix.slice(0, 7);
}

function formatConfigSourceLabel(source) {
  const normalized = String(source || "").trim();
  if (!normalized) return "";
  return normalized
    .split(/[-_]/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function releaseBadgeTone(configSource) {
  const normalized = String(configSource || "").toLowerCase();
  if (!normalized || normalized === "release") return "muted";
  if (normalized === "unavailable") return "critical";
  return "warning";
}

function renderReleaseBadge(releaseDigest, configSource, workerBuildSha) {
  if (!releaseDigest && !configSource && !workerBuildSha) return null;

  const normalizedSource = String(configSource || "").toLowerCase();
  const isNonRelease = normalizedSource && normalizedSource !== "release";
  const digestPart = shortDigest(releaseDigest);
  const label = isNonRelease
    ? formatConfigSourceLabel(configSource)
    : digestPart
      ? `Rel ${digestPart}`
      : "Release";

  const title = [
    releaseDigest ? `Release digest: ${releaseDigest}` : null,
    configSource ? `Config source: ${formatConfigSourceLabel(configSource)}` : null,
    workerBuildSha ? `Worker build: ${workerBuildSha}` : null,
  ]
    .filter(Boolean)
    .join(" | ");

  return (
    <span
      className={`summary-release-pill ${releaseBadgeTone(configSource)}`}
      title={title || undefined}
    >
      {label}
    </span>
  );
}

const SUMMARY_PAGE_SIZE = 25;
const SUMMARY_PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
const SUMMARY_FILTER_DEFAULTS = {
  direction: "all",
  duration: "all",
  followUp: "all",
};
const SUMMARY_FILTER_SECTIONS = [
  {
    title: "Call Details",
    fields: [
      {
        name: "direction",
        label: "Direction",
        type: "select",
        options: [
          { value: "all", label: "Both" },
          { value: "inbound", label: "Inbound" },
          { value: "outbound", label: "Outbound" },
        ],
      },
      {
        name: "duration",
        label: "Duration",
        type: "select",
        options: [
          { value: "all", label: "All" },
          { value: "short", label: "Short <2 min" },
          { value: "medium", label: "Medium 2-5 min" },
          { value: "long", label: "Long >5 min" },
        ],
      },
      {
        name: "followUp",
        label: "Follow-up",
        type: "select",
        options: [
          { value: "all", label: "All" },
          { value: "yes", label: "Yes" },
          { value: "no", label: "No" },
        ],
      },
    ],
  },
];
const SUMMARY_EXPORT_HEADERS = [
  "Date",
  "Type",
  "Contact",
  "Headline",
  "RootCallSID",
];

const normalizeCsvText = (value) =>
  String(value ?? "")
    .replace(/\r?\n+/g, " | ")
    .replace(/\s+/g, " ")
    .trim();

const csvEscape = (value) => {
  const normalized = normalizeCsvText(value);

  if (/[",\n]/.test(normalized)) {
    return `"${normalized.replace(/"/g, '""')}"`;
  }

  return normalized;
};

// Excel auto-detects a date/time-looking string in a CSV and silently re-parses it into its own
// date serial number, which then renders as "#####" once the column isn't wide enough to show it
// (a plain text string never has this problem - only something Excel recognizes as a real date).
// ="..." forces Excel to keep it as literal text instead, no matter the column width.
const csvForceText = (value) => `="${normalizeCsvText(value).replace(/"/g, '""')}"`;

const downloadCsvFile = (content, fileName) => {
  const blob = new Blob(["﻿", content], {
    type: "text/csv;charset=utf-8;",
  });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");

  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};


const buildSummaryCsv = (rows, timeZone = DEFAULT_PRACTICE_TIMEZONE) => {
  const csvLines = [SUMMARY_EXPORT_HEADERS.join(",")];

  rows.forEach((row) => {
    csvLines.push(
      [
        row?.date ? csvForceText(formatInPracticeTimezone(row.date, timeZone)) : csvEscape(""),
        csvEscape(
          String(row?.direction || "").replace(/^\w/, (char) =>
            char.toUpperCase(),
          ),
        ),
        csvEscape(maskPhone(row?.contactNumber || row?.phoneNumber || "")),
        csvEscape(row?.headline || ""),
        csvEscape(row?.rootCallSID || row?.rootCallSid || ""),
      ].join(","),
    );
  });

  return csvLines.join("\r\n");
};

export const InboundCallSummary = ({
  startDate = "",
  endDate = "",
  hasExplicitDateRange = false,
  onRangeChange = () => {},
}) => {
  const {
    paginatedRequest,
    usersPostRequest,
    token,
    PracticeId,
  } =
    useContextStateManagement();
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(SUMMARY_PAGE_SIZE);
  const [totalRows, setTotalRows] = useState(0);
  // The practice's own configured timezone, resolved server-side alongside the date-filtered
  // rows above - rendering their timestamps converted to this same zone (instead of the
  // browser's local zone) is what keeps "what day" a call falls on agreeing with the filter.
  const [practiceTimezone, setPracticeTimezone] = useState(DEFAULT_PRACTICE_TIMEZONE);
  const [rows, setRows] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [modalShow, setModalShow] = useState(false);
  const [activeModal, setActiveModal] = useState("transcript");
  const [transcriptRecord, setTranscriptRecord] = useState("");
  const [summary, setSummary] = useState("");
  const [callRecordings, setCallRecordings] = useState([]);
  const [selectedRow, setSelectedRow] = useState(null);
  const [isRecordingModalOpen, setIsRecordingModalOpen] = useState(false);
  const [isRecordingLoading, setIsRecordingLoading] = useState(false);
  const [recordingError, setRecordingError] = useState("");
  const [selectedRecordingUrl, setSelectedRecordingUrl] = useState("");
  const [toast, setToast] = useState({ open: false, message: "" });
  const [actionMenuAnchor, setActionMenuAnchor] = useState(null);
  const [actionMenuRow, setActionMenuRow] = useState(null);
  const [isFilterDialogOpen, setIsFilterDialogOpen] = useState(false);
  const [filters, setFilters] = useState(SUMMARY_FILTER_DEFAULTS);
  const [isClientAlertsModalOpen, setIsClientAlertsModalOpen] = useState(false);
  const inFlightReadsRef = useRef(new Set());
  const logSummary = (...args) => console.log("[SummaryTab]", ...args);

  const normalizeSid = (value) => String(value || "").trim();

  const revokeSelectedRecordingUrl = () => {
    setSelectedRecordingUrl((currentUrl) => {
      // Issue #18: currentUrl is now either a blob: object URL (legacy Twilio-proxy fallback,
      // needs revoking) or a presigned https S3 URL (issue #17, nothing to revoke).
      if (currentUrl && currentUrl.startsWith("blob:")) {
        URL.revokeObjectURL(currentUrl);
      }
      return "";
    });
  };

  const findRecordingInList = (row, recordings) => {
    const rowRecordingSid = normalizeSid(row?.recordingSid);
    const rowCallSid = normalizeSid(row?.callSid);
    const rowRootCallSid = normalizeSid(row?.rootCallSid);

    return (recordings || []).find((item) => {
      const recordingSid = normalizeSid(item?.sid);
      const recordingCallSid = normalizeSid(item?.callSid);
      const recordingRootCallSid = normalizeSid(item?.rootCallSid);
      const relatedCallSids = Array.isArray(item?.relatedCallSids)
        ? item.relatedCallSids.map((sid) => normalizeSid(sid)).filter(Boolean)
        : [];

      return (
        (rowRecordingSid && recordingSid === rowRecordingSid) ||
        (rowCallSid && recordingCallSid === rowCallSid) ||
        (rowCallSid && recordingRootCallSid === rowCallSid) ||
        (rowCallSid && relatedCallSids.includes(rowCallSid)) ||
        (rowRootCallSid && recordingCallSid === rowRootCallSid) ||
        (rowRootCallSid && recordingRootCallSid === rowRootCallSid) ||
        (rowRootCallSid && relatedCallSids.includes(rowRootCallSid))
      );
    });
  };

  const findRecordingForRow = (row) => findRecordingInList(row, callRecordings);

  const markRowAsRead = async (row) => {
    if (!row?.transcriptId) return;
    const transcriptId = row.transcriptId;
    const summaryKey =
      row?.summaryKey || `${row?.summarySource || "inbound"}:${transcriptId}`;

    if (inFlightReadsRef.current.has(summaryKey)) return;
    const currentRow = rows.find((item) => item.summaryKey === summaryKey);
    if (currentRow?.isRead || currentRow?.summarySource !== "inbound") return;

    logSummary("markRead start", {
      practiceId: PracticeId,
      transcriptId,
      summarySource: currentRow.summarySource,
    });
    inFlightReadsRef.current.add(summaryKey);
    setRows((prev) =>
      prev.map((item) =>
        item.summaryKey === summaryKey ? { ...item, isRead: true } : item,
      ),
    );

    try {
      await markSummaryReadApi(
        usersPostRequest,
        token,
        transcriptId,
        PracticeId,
        currentRow.summarySource,
      );
      logSummary("markRead success", {
        practiceId: PracticeId,
        transcriptId,
        summarySource: currentRow.summarySource,
      });
    } catch (err) {
      logSummary("markRead error", {
        practiceId: PracticeId,
        transcriptId,
        summarySource: currentRow?.summarySource,
        error: err?.message || String(err),
      });
      setRows((prev) =>
        prev.map((item) =>
          item.summaryKey === summaryKey ? { ...item, isRead: false } : item,
        ),
      );
      setToast({ open: true, message: "Failed to mark summary as read." });
    } finally {
      inFlightReadsRef.current.delete(summaryKey);
    }
  };

  const mapSummaryRow = (item) => ({
    id:
      item.summaryKey ||
      `${item.summarySource || "inbound"}:${item.transcriptId}`,
    summaryKey:
      item.summaryKey ||
      `${item.summarySource || "inbound"}:${item.transcriptId}`,
    transcriptId: item.transcriptId,
    isRead: item.isRead ?? false,
    direction: item.direction || item.summarySource || "inbound",
    summarySource: item.summarySource || "inbound",
    ...item,
  });

  const buildActiveFilterParams = () => ({
    search: debouncedSearch,
    // The top-bar DateRangePicker (startDate/endDate props from Home.jsx) is now always
    // populated with an auto-computed month-to-date default (for Inbound/Outbound's sake - see
    // useConsoleSearch), so startDate/endDate themselves can no longer tell us whether the user
    // actually chose a range. Branch on hasExplicitDateRange instead: until the user explicitly
    // picks (or clears) a range on any of the three tabs sharing this state, Summary keeps
    // sending empty strings so the backend's own 90-day default (resolveSummaryDateRange) keeps
    // applying, unchanged from before this default was introduced.
    startDate: hasExplicitDateRange ? startDate : "",
    endDate: hasExplicitDateRange ? endDate : "",
    direction: filters.direction !== "all" ? filters.direction : "",
    duration: filters.duration !== "all" ? filters.duration : "",
    followUp: filters.followUp !== "all" ? filters.followUp : "",
  });

  const loadSummaryPage = async ({ signal } = {}) => {
    setLoading(true);
    setError("");
    logSummary("fetch start", { practiceId: PracticeId, page });
    try {
      const result = await getInboundCallSummariesPaginated(
        paginatedRequest,
        token,
        PracticeId,
        {
          limit: pageSize,
          offset: page * pageSize,
          ...buildActiveFilterParams(),
        },
      );
      if (signal && signal.aborted) return;

      if (result.error) {
        logSummary("fetch error", {
          practiceId: PracticeId,
          error: result.error,
        });
        setError("Failed to load call summaries.");
        setRows([]);
        setTotalRows(0);
        return;
      }

      setRows((result.rows || []).map(mapSummaryRow));
      setTotalRows(result.total);
      if (result.practiceTimezone) {
        setPracticeTimezone(result.practiceTimezone);
      }
      logSummary("fetch success", {
        practiceId: PracticeId,
        rowCount: result.rows.length,
        total: result.total,
      });
    } catch (err) {
      if (signal && signal.aborted) return;
      logSummary("fetch error", {
        practiceId: PracticeId,
        error: err?.message || String(err),
      });
      setError("Failed to load call summaries.");
      setRows([]);
      setTotalRows(0);
    } finally {
      if (!signal || !signal.aborted) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    const handle = setTimeout(() => {
      setDebouncedSearch(searchQuery.trim());
      setPage(0);
    }, 350);
    return () => clearTimeout(handle);
  }, [searchQuery]);

  useEffect(() => {
    setPage(0);
  }, [startDate, endDate]);

  useEffect(() => {
    const controller = new AbortController();
    loadSummaryPage({ signal: controller.signal });
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    paginatedRequest,
    token,
    PracticeId,
    page,
    pageSize,
    debouncedSearch,
    startDate,
    endDate,
    filters.direction,
    filters.duration,
    filters.followUp,
  ]);

  useEffect(() => {
    let isMounted = true;

    const loadRecordings = async () => {
      try {
        const response = await axios.get("/api/getInboundCallRecordings", {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          params: {
            PracticeId,
          },
        });
        if (isMounted) {
          setCallRecordings(Array.isArray(response.data) ? response.data : []);
        }
      } catch (err) {
        if (isMounted) {
          logSummary("recordings fetch error", {
            practiceId: PracticeId,
            error: err?.message || String(err),
          });
          setCallRecordings([]);
        }
      }
    };

    if (token && PracticeId) {
      loadRecordings();
    } else if (isMounted) {
      setCallRecordings([]);
    }

    return () => {
      isMounted = false;
    };
  }, [token, PracticeId]);

  useEffect(() => {
    return () => {
      revokeSelectedRecordingUrl();
    };
  }, []);

  const handleCloseModal = () => {
    setModalShow(false);
    setTranscriptRecord("");
    setSummary("");
  };

  const handleCloseRecordingModal = () => {
    setIsRecordingModalOpen(false);
    setSelectedRow(null);
    setRecordingError("");
    setIsRecordingLoading(false);
    revokeSelectedRecordingUrl();
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  const handleSearchKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
    }
  };

  const handleOpenActionMenu = (event, row) => {
    setActionMenuAnchor(event.currentTarget);
    setActionMenuRow(row);
  };

  const handleCloseActionMenu = () => {
    setActionMenuAnchor(null);
    setActionMenuRow(null);
  };

  const handleSummaryAction = () => {
    if (!actionMenuRow) return;
    handleCloseActionMenu();
    summaryHandle(actionMenuRow);
  };

  const handleTranscriptAction = () => {
    if (!actionMenuRow) return;
    handleCloseActionMenu();
    transcriptHandle(actionMenuRow);
  };

  const handleRecordingAction = () => {
    if (!actionMenuRow) return;
    handleCloseActionMenu();
    recordingHandle(actionMenuRow);
  };

  const buildHeadlineTooltipText = (summaryText, headlineText) => {
    const normalizedSummary = String(summaryText || "").trim();
    if (!normalizedSummary) return headlineText || "";

    const sectionSplitPattern =
      /\n\s*(?:[-*]\s*)?(?:outcome|next\s*steps)\s*:/i;
    const primarySection =
      normalizedSummary.split(sectionSplitPattern)[0]?.trim() ||
      normalizedSummary;

    return primarySection;
  };

  // The file name should match whichever direction is currently filtered - it used to always say
  // "InboundSummary" even when filtered to Outbound only (the rows inside were always correct,
  // just the name on the download wasn't).
  const summaryExportNamePrefix =
    filters.direction === "outbound"
      ? "OutboundSummary"
      : filters.direction === "inbound"
        ? "InboundSummary"
        : "CallSummary";

  const handleExportSummary = async () => {
    setError("");
    try {
      const result = await getInboundCallSummaries(
        paginatedRequest,
        token,
        PracticeId,
        buildActiveFilterParams(),
      );

      if (result.error) {
        setToast({ open: true, message: "Failed to export call summaries." });
        return;
      }

      const csvContent = buildSummaryCsv(
        (result.rows || []).map(mapSummaryRow),
        result.practiceTimezone || practiceTimezone,
      );

      downloadCsvFile(
        csvContent,
        `${summaryExportNamePrefix}_${moment().format("YYYYMMDD_HHmmss")}.csv`,
      );
    } catch (err) {
      setToast({ open: true, message: "Failed to export call summaries." });
    }
  };

  const handleExportCurrentPage = () => {
    const csvContent = buildSummaryCsv(rows, practiceTimezone);
    downloadCsvFile(
      csvContent,
      `${summaryExportNamePrefix}_page${page + 1}_${moment().format("YYYYMMDD_HHmmss")}.csv`,
    );
  };

  const handleFilterChange = (name, value) => {
    setFilters((current) => ({
      ...current,
      [name]: value,
    }));
  };

  const handleApplyFilters = () => {
    setPage(0);
    setIsFilterDialogOpen(false);
  };

  const handleResetFilters = () => {
    setFilters(SUMMARY_FILTER_DEFAULTS);
    setPage(0);
  };

  const transcriptHandle = (row) => {
    logSummary("click transcript", {
      practiceId: PracticeId,
      transcriptId: row?.transcriptId,
    });
    markRowAsRead(row);
    setActiveModal("transcript");
    setTranscriptRecord(row?.transcript || "");
    setSummary("");
    setModalShow(true);
  };

  const summaryHandle = (row) => {
    logSummary("click summary", {
      practiceId: PracticeId,
      transcriptId: row?.transcriptId,
    });
    markRowAsRead(row);
    setActiveModal("summary");
    setSummary(row?.summary || "");
    setTranscriptRecord("");
    setModalShow(true);
  };

  const recordingHandle = async (row) => {
    setSelectedRow(row);
    setRecordingError("");
    setIsRecordingModalOpen(true);

    revokeSelectedRecordingUrl();
    setIsRecordingLoading(true);

    try {
      let resolvedRecordingSid =
        normalizeSid(row?.recordingSid) ||
        normalizeSid(findRecordingForRow(row)?.sid);

      if (!resolvedRecordingSid) {
        const response = await axios.get("/api/getInboundCallRecordings", {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          params: {
            PracticeId,
          },
        });
        const recordings = Array.isArray(response.data) ? response.data : [];
        setCallRecordings(recordings);
        resolvedRecordingSid = normalizeSid(
          findRecordingInList(row, recordings)?.sid,
        );
      }

      if (!resolvedRecordingSid) {
        setRecordingError("Recording not available.");
        return;
      }

      // Issue #18: resolves either the presigned S3 URL (recording already uploaded, issue #17)
      // or the legacy Twilio-proxied blob: object URL (not yet uploaded) into one string usable
      // directly as the <audio> element's src.
      const playbackUrl = await fetchRecordingPlaybackUrl(
        token,
        resolvedRecordingSid,
        PracticeId,
      );
      setSelectedRecordingUrl(playbackUrl);
    } catch (err) {
      logSummary("recording media fetch error", {
        practiceId: PracticeId,
        recordingSid: normalizeSid(row?.recordingSid) || null,
        error: err?.message || String(err),
      });
      setRecordingError("Failed to load recording.");
    } finally {
      setIsRecordingLoading(false);
    }
  };

  const columns = [
    {
      field: "dateType",
      headerName: "Date / Type",
      width: 180,
      minWidth: 180,
      align: "left",
      headerAlign: "left",
      headerClassName: "summary-column-header",
      cellClassName: "summary-date-type-wrap-cell",
      renderCell: (params) =>
        params.row?.date ? (
          <div
            className={`summary-date-cell ${
              params.row?.isRead ? "summary-row-read" : "summary-row-unread"
            }`}
          >
            <span className="summary-date-line">
              {!params.row?.isRead && <span className="summary-unread-dot" />}
              <span className="summary-date-text">
                {formatUsDateInPracticeTimezone(params.row.date, practiceTimezone)}
              </span>
            </span>
            <span className="summary-type-text">
              {String(params.row?.direction || "").replace(/^\w/, (char) =>
                char.toUpperCase(),
              )}
            </span>
          </div>
        ) : (
          ""
        ),
    },
    {
      field: "contactNumber",
      headerName: "Contact",
      width: 140,
      minWidth: 140,
      align: "left",
      headerAlign: "left",
      headerClassName: "summary-column-header",
      cellClassName: "summary-contact-wrap-cell",
      renderCell: (params) => (
        <span
          className={`summary-contact ${
            params.row?.isRead ? "summary-row-read" : "summary-row-unread"
          }`}
        >
          {params.value || ""}
        </span>
      ),
    },
    {
      field: "headline",
      headerName: "Headline",
      flex: 1,
      minWidth: 360,
      align: "left",
      headerAlign: "left",
      headerClassName: "summary-column-header",
      cellClassName: "summary-headline-wrap-cell",
      renderCell: (params) => {
        if (!params.value) {
          return (
            <span className="summary-headline summary-headline-blank">
              {params.row?.blankReason || "No summary available"}
            </span>
          );
        }

        return (
          <Tooltip
            title={
              <span className="summary-headline-tooltip-text">
                {buildHeadlineTooltipText(params.row?.summary, params.value)}
              </span>
            }
            placement="top"
            arrow
            slotProps={{
              tooltip: {
                className: "summary-headline-tooltip",
              },
              arrow: {
                className: "summary-headline-tooltip-arrow",
              },
            }}
          >
            <span
              className={`summary-headline ${
                params.row?.isRead ? "summary-row-read" : "summary-row-unread"
              }`}
            >
              {params.value}
            </span>
          </Tooltip>
        );
      },
    },
    {
      field: "rootCallSID",
      headerName: "RootCallSID",
      width: 300,
      minWidth: 300,
      align: "left",
      headerAlign: "left",
      headerClassName: "summary-column-header",
      cellClassName: "summary-contact-wrap-cell summary-nowrap-cell",
      renderCell: (params) => (
        <span
          className={`summary-contact ${
            params.row?.isRead ? "summary-row-read" : "summary-row-unread"
          }`}
          title={params.row?.rootCallSID || params.row?.rootCallSid || ""}
        >
          {params.row?.rootCallSID || params.row?.rootCallSid || ""}
        </span>
      ),
    },
    {
      field: "release",
      headerName: "Release",
      width: 140,
      minWidth: 140,
      align: "left",
      headerAlign: "left",
      headerClassName: "summary-column-header",
      sortable: false,
      filterable: false,
      renderCell: (params) =>
        renderReleaseBadge(
          params.row?.releaseDigest,
          params.row?.configSource,
          params.row?.workerBuildSha,
        ),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 100,
      minWidth: 100,
      align: "center",
      headerAlign: "center",
      headerClassName: "summary-column-header summary-column-header-actions",
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      cellClassName: "summary-actions-cell",
      renderCell: (params) => (
        <Tooltip title="Actions">
          <button
            type="button"
            className="summary-action-trigger"
            aria-label="Open summary actions"
            onClick={(event) => handleOpenActionMenu(event, params.row)}
          >
            <FontAwesomeIcon icon={faEllipsisVertical} />
          </button>
        </Tooltip>
      ),
    },
  ];

  const actionMenuHasRecording = Boolean(
    actionMenuRow?.recordingSid || findRecordingForRow(actionMenuRow)?.sid,
  );

  const summaryPageCount = Math.max(1, Math.ceil(totalRows / pageSize));

  return (
    <>
      <div className="summary-call-table-shell">
        <div className="summary-call-table">
          <div className="summary-call-table-topbar">
            <div className="summary-call-table-topbar-left">
              <div className="summary-searchbar">
                <input
                  type="text"
                  className="summary-search-input"
                  placeholder="Search by RootCallSID, Contact ...."
                  value={searchQuery}
                  onChange={handleSearchChange}
                  onKeyDown={handleSearchKeyDown}
                />
                <button
                  type="button"
                  className="summary-search-button"
                  aria-label="Search summaries"
                >
                  <FontAwesomeIcon icon={faMagnifyingGlass} />
                </button>
              </div>
            </div>
            <div className="summary-call-table-topbar-controls">
              <div className="summary-call-table-range">
                <DateRangePicker
                  startDate={startDate}
                  endDate={endDate}
                  onRangeChange={onRangeChange}
                />
                <button
                  type="button"
                  className="home-date-range-picker__trigger"
                  onClick={() => setIsFilterDialogOpen(true)}
                  aria-label="Open summary filters"
                >
                  <span className="home-date-range-picker__trigger-icon">
                    <Filter size={16} strokeWidth={2.1} />
                  </span>
                  <span className="home-date-range-picker__trigger-text">Filter By</span>
                </button>
              </div>
            </div>
            <div className="summary-call-table-topbar-right">
              <PageActionMenu
                onDownload={handleExportSummary}
                onDownloadCurrentPage={handleExportCurrentPage}
                onClientAlertsTab={() => setIsClientAlertsModalOpen(true)}
                downloadLabel="Download All (CSV)"
                downloadCurrentPageLabel="Download Current Page (CSV)"
              />
            </div>
          </div>
          <div className="summary-call-table-grid">
            <DataGrid
              rows={rows}
              columns={columns}
              getRowHeight={() => "auto"}
              columnHeaderHeight={48}
              scrollbarSize={8}
              loading={loading}
              paginationMode="server"
              rowCount={totalRows}
              paginationModel={{ page, pageSize }}
              onPaginationModelChange={(model) => setPage(model.page)}
              pageSizeOptions={[pageSize]}
              hideFooter
              disableColumnMenu
              disableRowSelectionOnClick
              sx={{
                border: 0,
                "& .MuiDataGrid-columnHeader, & .MuiDataGrid-cell": {
                  justifyContent: "flex-start",
                  alignItems: "center",
                  px: 1.75,
                },
                "& .MuiDataGrid-columnHeaderTitleContainer, & .MuiDataGrid-cellContent":
                  {
                    width: "100%",
                    justifyContent: "flex-start",
                    textAlign: "left",
                  },
                "& .MuiDataGrid-columnHeaderTitleContainerContent": {
                  width: "100%",
                  justifyContent: "flex-start",
                },
                "& .MuiDataGrid-cell": {
                  py: 1.2,
                },
              }}
            />
          </div>
          <div className="inbound-call-table-footer">
            <div className="inbound-call-table-footer-primary">
              <div className="inbound-call-table-count">
                {totalRows > 0
                  ? `Showing ${page * pageSize + 1}-${Math.min(
                      (page + 1) * pageSize,
                      totalRows,
                    )} of ${totalRows}`
                  : ""}
              </div>
            </div>
            <div className="inbound-call-table-footer-controls">
              <div className="inbound-call-table-page-size">
                <span className="inbound-call-table-page-size-icon" aria-hidden="true">
                  <List size={16} strokeWidth={2} />
                </span>
                <label
                  htmlFor="summary-call-table-page-size"
                  className="inbound-call-table-sr-only"
                >
                  Rows per page
                </label>
                <div className="inbound-call-table-page-size-select-wrap">
                  <select
                    id="summary-call-table-page-size"
                    className="inbound-call-table-page-size-select"
                    value={pageSize}
                    onChange={(event) => {
                      setPageSize(Number(event.target.value));
                      setPage(0);
                    }}
                  >
                    {SUMMARY_PAGE_SIZE_OPTIONS.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                  <span
                    className="inbound-call-table-page-size-chevron"
                    aria-hidden="true"
                  >
                    <ChevronDown size={16} strokeWidth={2} />
                  </span>
                </div>
              </div>
              <span className="inbound-call-table-footer-divider" aria-hidden="true" />
              <div className="inbound-call-table-pagination">
                <button
                  type="button"
                  onClick={() => setPage((current) => Math.max(current - 1, 0))}
                  disabled={page === 0}
                  className="inbound-call-table-nav-button"
                  aria-label="Previous page"
                >
                  <ChevronLeft size={16} strokeWidth={2.1} />
                </button>
                <div className="inbound-call-table-page-indicator">
                  {page + 1} / {summaryPageCount}
                </div>
                <button
                  type="button"
                  onClick={() =>
                    setPage((current) => Math.min(current + 1, summaryPageCount - 1))
                  }
                  disabled={page >= summaryPageCount - 1}
                  className="inbound-call-table-nav-button"
                  aria-label="Next page"
                >
                  <ChevronRight size={16} strokeWidth={2.1} />
                </button>
              </div>
            </div>
          </div>
          <CallFilterDialog
            open={isFilterDialogOpen}
            title="Summary Filters"
            description="Narrow summary results without changing the primary date range."
            sections={SUMMARY_FILTER_SECTIONS}
            values={filters}
            onChange={handleFilterChange}
            onClose={() => setIsFilterDialogOpen(false)}
            onApply={handleApplyFilters}
            onReset={handleResetFilters}
          />
          <ClientAlertConfigModal
            open={isClientAlertsModalOpen}
            practiceId={PracticeId}
            onClose={() => setIsClientAlertsModalOpen(false)}
          />
        </div>
      </div>

      <Menu
        anchorEl={actionMenuAnchor}
        open={Boolean(actionMenuAnchor)}
        onClose={handleCloseActionMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{ className: "summary-actions-menu" }}
        MenuListProps={{ className: "summary-actions-menu-list" }}
      >
        <MenuItem
          className="summary-actions-menu-item"
          onClick={handleSummaryAction}
        >
          View Summary
        </MenuItem>
        <MenuItem
          className="summary-actions-menu-item"
          onClick={handleTranscriptAction}
        >
          View Transcript
        </MenuItem>
        <MenuItem
          className="summary-actions-menu-item"
          onClick={handleRecordingAction}
          disabled={!actionMenuHasRecording}
        >
          {actionMenuHasRecording ? "Play Recording" : "Recording Not Available"}
        </MenuItem>
      </Menu>

      {!loading && error && <div className="text-danger">{error}</div>}

      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={() => setToast({ open: false, message: "" })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity="error"
          onClose={() => setToast({ open: false, message: "" })}
          variant="filled"
        >
          {toast.message}
        </Alert>
      </Snackbar>

      <Modal
        className="popup"
        show={modalShow}
        onHide={() => setModalShow(false)}
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {activeModal === "transcript" ? "Transcripts" : "Summary"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {activeModal === "transcript" ? (
            <div style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
              {transcriptRecord || ""}
            </div>
          ) : (
            <div style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
              {summary || ""}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button className="simple-btn" onClick={handleCloseModal}>
            Close
          </button>
        </Modal.Footer>
      </Modal>

      <Modal
        className="popup"
        show={isRecordingModalOpen}
        onHide={handleCloseRecordingModal}
      >
        <Modal.Header closeButton>
          <Modal.Title>Recording</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-2">
            <strong>Contact:</strong> {selectedRow?.contactNumber || ""}
          </div>
          <div className="mb-2">
            <strong>Date:</strong>{" "}
            {selectedRow?.date
              ? formatUsDateInPracticeTimezone(selectedRow.date, practiceTimezone)
              : ""}
          </div>
          {selectedRow?.headline ? (
            <div className="mb-3">
              <strong>Headline:</strong> {selectedRow.headline}
            </div>
          ) : null}
          {isRecordingLoading ? (
            <div>Loading recording...</div>
          ) : recordingError ? (
            <div>{recordingError}</div>
          ) : selectedRecordingUrl ? (
            <audio controls src={selectedRecordingUrl} />
          ) : (
            <div>No recording available.</div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button className="simple-btn" onClick={handleCloseRecordingModal}>
            Close
          </button>
        </Modal.Footer>
      </Modal>
    </>
  );
};
